import React from 'react';
import { Button, Col, Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { fi } from 'date-fns/locale';
import { BlockedPeriodDetails } from '../blockedPeriodDetailsSlice';
import { BLOCKED_PERIOD_TYPE } from '../../../common/constants';
import { enumToOptions } from '../../../common/utils/enumUtils';

interface BlockedPeriodFormProps {
  details: BlockedPeriodDetails;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDateChange: (field: string, value: string | Date) => void;
  handleSelectChange: (newValue: { value: string; label: string }) => void;
  handleCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isNewBlockedPeriod?: boolean;
  isDisabled?: boolean;
}

const blockedPeriodTypeOptions = enumToOptions(BLOCKED_PERIOD_TYPE);

export const BlockedPeriodForm: React.FC<BlockedPeriodFormProps> = ({
  details,
  handleInputChange,
  handleDateChange,
  handleSelectChange,
  handleCheckboxChange,
  isNewBlockedPeriod = false,
  isDisabled = false,
}) => {
  // Filter out iCal import type when creating new blocked period
  // Backend returns enum name (e.g., "ICalImport"), which is the option value
  const availableTypeOptions = isNewBlockedPeriod
    ? blockedPeriodTypeOptions.filter(option => option.value !== 'ICalImport')
    : blockedPeriodTypeOptions;

  return (
    <>
      <Form.Group className="mb-16">
        <Form.Label>Alkaa</Form.Label>
        <div className="w-100 position-relative">
          <DatePicker
            locale={fi}
            id="startDate"
            name="startDate"
            dateFormat="dd.MM.yyyy"
            selected={details?.startDate ? new Date(details.startDate) : null}
            onChange={date => handleDateChange('startDate', date)}
            className="form-control"
            showWeekNumbers
            showYearDropdown
            scrollableYearDropdown
            disabled={isDisabled}
          />
          <Button
            variant="link"
            className="pe-none position-absolute end-0 top-0"
            id="button-addon2"
          >
            <span className="bi bi-calendar2-event" />
          </Button>
        </div>
      </Form.Group>
      <Form.Group className="mb-16">
        <Form.Label>Päättyy</Form.Label>
        <div className="w-100 position-relative">
          <DatePicker
            locale={fi}
            id="endDate"
            name="endDate"
            dateFormat="dd.MM.yyyy"
            selected={details?.endDate ? new Date(details.endDate) : null}
            onChange={date => handleDateChange('endDate', date)}
            className="form-control"
            minDate={details?.startDate ?? null}
            disabled={!details?.startDate || isDisabled}
            showWeekNumbers
            showYearDropdown
            scrollableYearDropdown
          />
          <Button
            variant="link"
            className="pe-none position-absolute end-0 top-0"
            id="button-addon2"
          >
            <span className="bi bi-calendar2-event" />
          </Button>
        </div>
      </Form.Group>
      <Form.Group className="mb-16">
        <Form.Label>Tyyppi</Form.Label>
        <Select
          inputId="type"
          name="type"
          placeholder="Valitse tyyppi"
          options={availableTypeOptions}
          onChange={handleSelectChange}
          value={availableTypeOptions.find(
            option => option.value === details?.type,
          )}
          isDisabled={isDisabled}
        />
      </Form.Group>
      <Form.Group className="mb-16">
        <Form.Label>Kuvaus</Form.Label>
        <Form.Control
          type="text"
          id="description"
          name="description"
          value={details?.description || ''}
          onChange={handleInputChange}
          disabled={isDisabled}
        />
      </Form.Group>
      <Form.Group controlId="isCopyable">
        <Form.Label>Kopioitava</Form.Label>
        <Form.Check
          type="checkbox"
          name="isCopyable"
          onChange={handleCheckboxChange}
          checked={details?.isCopyable}
          disabled={isDisabled}
        />
      </Form.Group>
    </>
  );
};
